/**
 * Reading documents out of the database.
 *
 * WHY THIS FILE IS SEPARATE FROM `documentService.ts`
 * -------------------------------------------------
 * `documentService.ts` is the WRITE path, and it is the only place a
 * transaction is opened for a document. Reads have none of that machinery and
 * none of its hazards: there is no atomicity to protect, no number to
 * allocate, and no stock to post. Mixing them would mean every read dragged in
 * a transaction scope it has no business opening, and the temptation to "just
 * fix it in the service" is exactly how a write-only service quietly grows a
 * second, unreviewed transaction.
 *
 * So: reads live here and are deliberately read-only.
 *
 * THE SHAPE OF THE PROBLEM
 * ------------------------
 * A document is stored as a HEADER row plus a LINES table. The renderers want
 * one `NormalizedDocument`. Getting from one to the other is where the subtle
 * failures live, and they are all of the same kind — a partial read that
 * renders confidently:
 *
 *   - header found, lines missing  -> an invoice with no items
 *   - lines found, header missing  -> an orphaned page
 *   - lines returned in arbitrary order -> items numbered 3,1,2
 *   - lines belonging to a different document -> a wrong invoice
 *
 * None of these throw. They produce a document that *looks* printable. So the
 * assembly is strict: a document with no lines, or with a line that does not
 * belong to it, is an error rather than a short page.
 */
import type { Table } from 'dexie';
import { normalizeDocument } from '@core/doc/normalizeDocument';
import type { NormalizedDocument } from '@core/doc/types';
import type { NormalizableDocument, NormalizableLine } from '@core/doc/normalizeDocument';
import type { Document, DocumentLine } from '../db/types';

export class DocumentReadError extends Error {
  constructor(
    readonly code: 'NOT_FOUND' | 'NO_LINES' | 'ORPHANED_LINE' | 'WRONG_BUSINESS' | 'UNREADABLE',
    message: string,
  ) {
    super(message);
    this.name = 'DocumentReadError';
  }
}

/** The tables a read needs. */
export interface ReadTables {
  documents: Table<Document, string>;
  documentLines: Table<DocumentLine, string>;
  products: Table<any, string>;
}

export interface LoadDocumentOptions {
  /**
   * Forwarded to `normalizeDocument`, which requires it on `NormalizeInput`.
   *
   * WHAT IT DOES TODAY: nothing. `NormalizeInput.businessStateCode` is declared
   * and never read anywhere in `core/doc`; `isInterState` is derived from the
   * document's STORED `supplyType` (normalizeDocument.ts:311).
   *
   * An earlier version of this file documented it as "decides INTRA vs INTER",
   * which was false, and wrote a test asserting the false rule — a test that
   * passed only because it asserted something else entirely. Both are gone.
   * The parameter is kept because the type demands it, not because it earns
   * its keep.
   *
   * If place of supply ever becomes computed, this is the seam — and the test
   * that must accompany it is one that FAILS when the value is ignored.
   */
  readonly businessStateCode: string;

  /**
   * The company the caller believes it is reading on behalf of.
   *
   * OPTIONAL on purpose. When it is supplied, a document belonging to any other
   * business is REFUSED rather than returned. When it is omitted the read is
   * unscoped, which some callers legitimately need — and turning a missing
   * argument into a hard failure would break them for no safety gained.
   *
   * WHY THIS IS NOT OPTIONAL IN THE UI. A document id is a global primary key,
   * so `db.documents.get(id)` happily returns another company's invoice. With
   * two real companies imported that is not a theoretical hole: switch company,
   * follow an old link, and company A's invoice prints under company B's letterhead
   * — the wrong GSTIN, the wrong bank account, on a real tax document. The
   * screen passes the active business id so this check can refuse.
   */
  readonly businessId?: string;
}

/**
 * Read one document and assemble it into a renderable model.
 *
 * @throws DocumentReadError if the header is missing, has no lines, or owns a
 *         line that does not belong to it.
 */
export async function loadDocument(
  db: ReadTables,
  documentId: string,
  opt: LoadDocumentOptions,
): Promise<NormalizedDocument> {
  const header = await db.documents.get(documentId);
  if (!header) {
    throw new DocumentReadError(
      'NOT_FOUND',
      `No document with id "${documentId}". The user is about to be shown a blank page ` +
        'that looks like a real document, so this must be loud.',
    );
  }

  // THE COMPANY BOUNDARY. Checked before anything is rendered, and before the
  // lines are even read, so no part of another company's document reaches the
  // screen. `db.documents.get` is keyed on the primary key alone, so without this
  // the only thing standing between a switched company and the wrong GSTIN on a
  // printed invoice is the absence of this check.
  if (opt.businessId !== undefined && header.businessId !== opt.businessId) {
    throw new DocumentReadError(
      'WRONG_BUSINESS',
      `Document ${header.docNumber} belongs to another company, not the one currently ` +
        'open. Refusing to show it, because printing one company\'s invoice under ' +
        'another\'s letterhead puts the wrong GSTIN on a real tax document.',
    );
  }

  // Read through the [documentId+lineNo] compound index rather than sorting in
  // memory: it is already declared, it returns lines in line-number order
  // directly from IndexedDB, and it turns two operations (scan, then sort) into
  // one. The sort below is then only a belt-and-braces guarantee for a caller
  // that assembled the array some other way.
  const lines = await db.documentLines
    .where('[documentId+lineNo]')
    .between([documentId, -Infinity], [documentId, Infinity])
    .toArray();

  if (lines.length === 0) {
    throw new DocumentReadError(
      'NO_LINES',
      `Document ${header.docNumber} has no lines. An invoice with no items is almost ` +
        'always a failed write, and rendering it anyway would print a plausible-looking ' +
        'page worth nothing.',
    );
  }

  // Fetch product names for lines that have a productId.
  const productIds = [...new Set(lines.map((l) => l.productId).filter((id): id is string => Boolean(id)))];
  const products = await db.products.where('id').anyOf(productIds).toArray();
  const productNameById = new Map(products.map((p) => [p.id, p.name]));

  // Add productName to each line for display in print/preview.
  const linesWithProductName = lines.map((l) => ({
    ...l,
    productName: l.productId ? productNameById.get(l.productId) : undefined,
  }));

  return assemble(header, linesWithProductName, opt.businessStateCode);
}

/**
 * Assemble a header and its lines into a renderable model.
 *
 * Exported separately from `loadDocument` because it is pure, and because the
 * migration's own fixtures need to render without a database. Anything that
 * can reach a `NormalizedDocument` should go through here, so the ordering and
 * the tax-split rules have exactly one implementation.
 */
export function assemble(
  header: Document,
  lines: readonly DocumentLine[],
  businessStateCode: string,
): NormalizedDocument {
  // The orphaned-line check lives HERE, not in `loadDocument`, because this is
  // the exported seam: a caller can hand `assemble()` an array from anywhere, and
  // a line belonging to a different document would put another invoice's items
  // on this page with every total still balancing. In `loadDocument` the same
  // check was unreachable - the `[documentId+lineNo]` index cannot return a
  // foreign line - and a test "covering" it passed even after the check was
  // deleted, because it was only testing Dexie's index filtering.
  const orphans = lines.filter((l) => l.documentId !== header.id);
  if (orphans.length > 0) {
    throw new DocumentReadError(
      'ORPHANED_LINE',
      `${orphans.length} line(s) passed for ${header.docNumber} belong to other ` +
        'documents. Refusing to render a page that mixes them.',
    );
  }
  // Sort by the stored line number, not by whatever order the index returned.
  // Dexie promises no ordering across a plain index scan, and an invoice whose
  // items print as 3, 1, 2 is a defect a reader notices immediately and a test
  // asserting on a Set would never see.
  const ordered = [...lines].sort((a, b) => a.lineNo - b.lineNo);

  return normalizeDocument({
    document: header as unknown as NormalizableDocument,
    lines: ordered as unknown as NormalizableLine[],
    businessStateCode,
  });
}

/**
 * Recent documents, newest first, for a list screen.
 *
 * Returns headers only. Assembling every document's lines to build a list would
 * be an N+1 query over the whole table, and the list has no use for the lines.
 *
 * The TOTAL is returned alongside the page, and that is the point. This used to
 * return a bare array capped at 25, with nothing on screen to say so: after
 * importing 471 documents the sidebar showed 25 and the user could not tell
 * whether they had lost the other 446 or whether the app simply had not asked
 * for them. A silent truncation reads as data loss, and on a books app that is
 * the one inference a user will make. The caller now says "showing 25 of 471".
 */
export interface RecentDocumentsPage {
  readonly documents: Document[];
  /** How many documents the business has in total, before `limit`. */
  readonly total: number;
  /** True when `total` exceeds what is being shown. */
  readonly truncated: boolean;
  /**
   * Party name per DOCUMENT ID, for the rows in `documents` only.
   *
   * The list is the one place a business owner looks to answer "who was this
   * for?" and `Document` links a party only by id. Showing that would make
   * every row unidentifiable, which is the same defect the doc-type label was
   * added to fix, one level up.
   *
   * The value is `partySnapshot.name` - the name FROZEN ON THE DOCUMENT, not the
   * current customer master. That is not a shortcut: it is the only version
   * that cannot contradict the invoice. See the comment at the build site.
   *
   * KEYED BY DOCUMENT ID, and this is the whole point of the map.
   *
   * It was keyed by party ID, and that silently destroyed the exact guarantee
   * the snapshot exists to provide. One customer with three invoices is one
   * map entry, so all three rows rendered whichever name happened to be
   * written LAST. Rename a customer, issue a second invoice, and the list
   * rewrote the FIRST invoice's frozen name too - so the row contradicted the
   * very invoice it links to. That is the two-screens-two-truths failure the
   * snapshot was introduced to prevent, reintroduced through the index.
   *
   * Fifty invoices for one customer carrying fifty different frozen names is
   * not duplication; it is fifty separate historical facts, and they are what
   * the list is for. The earlier "carry the name once" reasoning was right for
   * a JOIN to the master and wrong for a snapshot, because a join has one
   * value per party while a snapshot has one per document.
   */
  readonly partyNamesByDocumentId: ReadonlyMap<string, string>;
}

/**
 * What the list is allowed to offer for a document, derived from its status.
 *
 * THIS FUNCTION IS THE LIFECYCLE, AS SEEN BY THE USER.
 * --------------------------------------------------------------
 * The service already refuses to edit or delete an issued document - but a
 * refusal the user discovers by typing an invoice and pressing Save is a poor
 * substitute for not offering the action. Worse, it teaches the user the app is
 * unreliable: the button was there, so pressing it must work.
 *
 * Deriving the actions from `status` in ONE place means the list, the document
 * screen and the write path cannot form three different opinions. It is a plain
 * function so that agreement is testable rather than a matter of three
 * developers reading the same code carefully.
 *
 * `canDelete` is deliberately false for an issued invoice: cancelling is a
 * separate, explicit, audited act, and a "Delete" button beside it invites the
 * user to destroy a tax document instead of recording that it was wrong.
 */
export interface DocumentActions {
  readonly canEdit: boolean;
  readonly canIssue: boolean;
  readonly canDelete: boolean;
  readonly canCancel: boolean;
  readonly canPrint: boolean;
}

export function documentActions(status: string): DocumentActions {
  switch (status) {
    case 'DRAFT':
      return {
        canEdit: true,
        canIssue: true,
        // A draft was never sent to anyone, so discarding it is not a record
        // event. Cancelling a DRAFT is what would be misleading.
        canDelete: true,
        canCancel: false,
        canPrint: true,
      };
    case 'ISSUED':
      // Frozen. Edit is false, and no preference can turn it on.
      return {
        canEdit: false,
        canIssue: false,
        canDelete: false,
        canCancel: true,
        canPrint: true,
      };
    case 'CANCELLED':
      // Kept for the record and still printable - a cancelled invoice is often
      // exactly what someone wants to see why they were charged.
      return {
        canEdit: false,
        canIssue: false,
        canDelete: false,
        canCancel: false,
        canPrint: true,
      };
    default:
      // An unknown status is treated as immutable rather than editable.
      // Defaulting to editable would let a future status - a partially imported
      // row, say - become editable by accident.
      return {
        canEdit: false,
        canIssue: false,
        canDelete: false,
        canCancel: false,
        canPrint: true,
      };
  }
}

export async function listRecentDocuments(
  db: ReadTables,
  businessId: string,
  limit?: number,
): Promise<Document[]>;
export async function listRecentDocuments(
  db: ReadTables,
  businessId: string,
  limit: number,
  withTotal: true,
): Promise<RecentDocumentsPage>;
export async function listRecentDocuments(
  db: ReadTables,
  businessId: string,
  limit: number,
  withTotal: true,
  docType?: Document['docType'],
  offset?: number,
): Promise<RecentDocumentsPage>;
export async function listRecentDocuments(
  db: ReadTables,
  businessId: string,
  limit: number,
  withTotal: false,
  docType: Document['docType'],
  offset?: number,
): Promise<Document[]>;
export async function listRecentDocuments(
  db: ReadTables,
  businessId: string,
  limit: number,
  withTotal: true,
  docType: Document['docType'] | undefined,
  offset?: number,
): Promise<RecentDocumentsPage>;
export async function listRecentDocuments(
  db: ReadTables,
  businessId: string,
  limit = 25,
  withTotal = false,
  docType?: Document['docType'],
  offset = 0,
): Promise<Document[] | RecentDocumentsPage> {
  const totalRequested = withTotal;
  const selectedType = docType;
  const rows = await db.documents.where('businessId').equals(businessId).toArray();
  // Sort in memory: these are already in IndexedDB, and a compound index over
  // (businessId, docDate) is a schema change to justify later, not now.
  const matching = selectedType ? rows.filter((row) => row.docType === selectedType) : rows;
  const sorted = matching.sort(
    (a, b) => b.docDate.localeCompare(a.docDate) || b.docNumber.localeCompare(a.docNumber),
  );
  const pageStart = Math.max(0, Math.floor(offset));
  const page = sorted.slice(pageStart, pageStart + Math.max(0, limit));
  if (!totalRequested) return page;

  /*
   * Names for the rows actually shown.
   *
   * READ FROM `partySnapshot`, NOT FROM THE `parties` TABLE - and that is the
   * whole reason this is four lines rather than a join.
   *
   * `Document` deliberately stores the customer as a frozen snapshot, because an
   * issued invoice must not change when the customer master is corrected. A list
   * that joined back to `parties` would therefore DISAGREE with the invoice it
   * links to: rename the customer, and the invoice still printed the old name
   * while the list showed the new one. Two screens, two truths, on the same row.
   *
   * Using the snapshot also keeps `ReadTables` at two tables. Widening it to
   * reach `parties` would let any caller of `loadDocument` pull in master data
   * it has no business having.
   *
   * `customerId ?? supplierId`, because the same table backs purchases, and a
   * purchase invoice has no customer at all.
   *
   * KEYED BY `d.id`. Keying by party id was correct-looking and wrong: parties
   * are shared, snapshots are not, so a shared key let the most recent document
   * overwrite the frozen names of every older one. Two invoices for one renamed
   * customer then rendered identically on the list while printing differently,
   * which is precisely the contradiction `partySnapshot` exists to prevent.
   */
  const partyNamesByDocumentId = new Map<string, string>();
  for (const d of page) {
    // Not guarded on party id any more. A document with no party still has a
    // frozen snapshot name (or an empty one), and the row must render whatever
    // the document actually carries rather than fall through to a dash.
    partyNamesByDocumentId.set(d.id, d.partySnapshot.name);
  }

  return {
    documents: page,
    total: sorted.length,
    truncated: sorted.length > page.length,
    partyNamesByDocumentId,
  };
}